import { Download, FileText, Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { useTemplatePreview } from '../../api/useTemplatePreview';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';

/**
 * Read-only Document Template step, for the view / review screens.
 *
 * Shows the same document the applicant saw, re-rendered from the session's
 * results — the generated PDF is not stored on the step, and re-rendering is free.
 */
export function TemplateDetail({
  session,
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  const { data, isLoading, error } = useTemplatePreview(session.id, step.id);

  useEffect(() => {
    // Nothing to submit from this view — the reviewer's verify button stays off.
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const pdfUrl = useMemo(
    () => (data ? `data:application/pdf;base64,${data.documentBase64}` : null),
    [data],
  );

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] py-12">
          <Loader2 size={18} className="animate-spin text-primary" />
          <span className="text-sm text-[#666666]">Rendering the document…</span>
        </div>
      ) : error || !data || !pdfUrl ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[#E5E7EB] py-12 text-center">
          <FileText size={22} className="text-[#9CA3AF]" />
          <p className="text-sm text-[#666666]">
            The document could not be rendered. Its template may have been changed or removed.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
          <div className="flex items-center gap-2 border-b border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <FileText size={14} className="text-primary" />
            </span>
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#041A3A]">
              {data.fileName}
            </span>
            <a
              href={pdfUrl}
              download={data.fileName}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-primary transition hover:bg-primary/10"
            >
              <Download size={13} />
              Download
            </a>
          </div>

          {/* Same viewer params as the applicant's screen — the browser's own PDF
              toolbar would otherwise cover the top of the document. */}
          <object
            data={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
            type="application/pdf"
            title={step.displayName}
            className="block h-[520px] w-full bg-[#F3F4F6]"
          >
            <div className="flex h-[520px] items-center justify-center p-6 text-center text-sm text-[#666666]">
              Your browser can't show the document inline.
            </div>
          </object>
        </div>
      )}
    </form>
  );
}
